import { formatWeekYearAsHumanReadable } from '@bestseller/retail-planning.styletimeline';
import { ColDef, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import { capitalCase } from 'change-case';
import { AuditDetailsModel } from 'src/domain/models/AuditDetailsModel';
import { AuditLogEnum } from 'src/infrastructure/rest-api/api-types';
import { components } from 'src/infrastructure/rest-api/generated';
import { formatStringDateTime } from 'src/utils/formatAsMonthYear';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';

interface FormatResult {
    oldValue: string;
    newValue: string;
}

interface FormatAuditLogExtraData {
    phaseoutProfiles?: components['schemas']['PhaseoutProfileModel'][];
    chainFurnitures?: components['schemas']['ChainFurnitureModel'][];
}

export const formatAuditLogChange = (
    detail: AuditDetailsModel,
    details: AuditDetailsModel[],
    colDefs: ColDef[],
    module: AuditLogEnum | null,
    extraData?: FormatAuditLogExtraData
): FormatResult | null => {
    switch (module) {
        case 'StyleTimeline':
            return formatStyleTimelineChange(detail, details, colDefs, extraData);
        case 'Turnover':
            return formatTurnoverChange(detail);
        case 'SalesCampaign':
            return formatSalesCampaignChange(detail);
    }
    return {
        oldValue: ((detail.oldValue as string) ?? '').toString(),
        newValue: ((detail.newValue as string) ?? '').toString(),
    };
};

export const formatSalesCampaignChange = (detail: AuditDetailsModel): FormatResult => {
    if (detail.fieldName === 'LastPublished') {
        return {
            oldValue: detail.oldValue ? formatStringDateTime(detail.oldValue as string) : '',
            newValue: detail.newValue ? formatStringDateTime(detail.newValue as string) : '',
        };
    }
    return {
        oldValue: detail.oldValue?.toString() ?? '',
        newValue: detail.newValue?.toString() ?? '',
    };
};

export const formatStyleTimelineChange = (
    detail: AuditDetailsModel,
    details: AuditDetailsModel[],
    colDefs: ColDef[],
    extraData?: FormatAuditLogExtraData
): FormatResult | null => {
    switch (detail.fieldName) {
        case 'ExitWeek': {
            const colDef = colDefs.find((c) => c.field === 'exitWeek');
            const exitWeekType = findMatchingDetail('ExitWeekType', detail, details);
            if (!exitWeekType) {
                return {
                    oldValue: formatWeekYearAsHumanReadable(detail.oldValue as number),
                    newValue: formatWeekYearAsHumanReadable(detail.newValue as number),
                };
            }
            return {
                oldValue: formatValue(
                    { exitWeek: detail.oldValue, exitWeekType: exitWeekType.oldValue },
                    colDef?.valueFormatter,
                    formatWeekYearAsHumanReadable
                ),
                newValue: formatValue(
                    { exitWeek: detail.newValue, exitWeekType: exitWeekType.newValue },
                    colDef?.valueFormatter,
                    formatWeekYearAsHumanReadable
                ),
            };
        }
        case 'NormClosureWeekExpected':
        case 'NormClosureWeek': {
            const colDef = colDefs.find((c) => c.field === 'normClosureWeek');
            return {
                oldValue: formatValue(detail.oldValue, colDef?.valueFormatter, formatWeekYearAsHumanReadable),
                newValue: formatValue(detail.newValue, colDef?.valueFormatter, formatWeekYearAsHumanReadable),
            };
        }
        case 'ExitWeekType': {
            const colDef = colDefs.find((c) => c.field === 'exitWeek');
            const exitWeek = findMatchingDetail('ExitWeek', detail, details);
            if (!exitWeek) {
                return {
                    oldValue: formatValue(
                        { exitWeek: null, exitWeekType: detail.oldValue },
                        colDef?.valueFormatter,
                        (val) => val
                    ),
                    newValue: formatValue(
                        { exitWeek: null, exitWeekType: detail.newValue },
                        colDef?.valueFormatter,
                        (val) => val
                    ),
                };
            }
            return null;
        }
        case 'PhaseoutProfileId': {
            const oldPhaseoutProfile = extraData?.phaseoutProfiles?.find((p) => p.id === detail.oldValue);
            const newPhaseoutProfile = extraData?.phaseoutProfiles?.find((p) => p.id === detail.newValue);

            const oldValue = detail.oldValue
                ? (oldPhaseoutProfile?.name ?? `<Deleted> (id=${detail.oldValue as number})`)
                : '';
            const newValue = detail.newValue
                ? (newPhaseoutProfile?.name ?? `<Deleted> (id=${detail.newValue as number})`)
                : '';
            return {
                oldValue,
                newValue,
            };
        }
        case 'ChainFurnitureId': {
            const oldChainFurniture = extraData?.chainFurnitures?.find((p) => p.id === detail.oldValue);
            const newChainFurniture = extraData?.chainFurnitures?.find((p) => p.id === detail.newValue);

            const oldValue = detail.oldValue
                ? (oldChainFurniture?.name ?? `<Deleted> (id=${detail.oldValue as number})`)
                : '';
            const newValue = detail.newValue
                ? (newChainFurniture?.name ?? `<Deleted> (id=${detail.newValue as number})`)
                : '';
            return {
                oldValue,
                newValue,
            };
        }
        case 'PackawayStartWeek':
        case 'PackawayEndWeek': {
            const colDef = colDefs.find((c) => c.field === detail.fieldName);
            return {
                oldValue: detail.oldValue
                    ? formatValue(detail.oldValue, colDef?.valueFormatter, formatWeekYearAsHumanReadable)
                    : '',
                newValue: detail.newValue
                    ? formatValue(detail.newValue, colDef?.valueFormatter, formatWeekYearAsHumanReadable)
                    : '',
            };
        }
    }
    return {
        oldValue: detail.oldValue?.toString() ?? '',
        newValue: detail.newValue?.toString() ?? '',
    };
};

function formatTurnoverChange(detail: AuditDetailsModel): FormatResult | null {
    if (detail.fieldName === 'SalesiVlcy') {
        return {
            oldValue: numberWithSpaces(detail.oldValue as number) ?? '',
            newValue: numberWithSpaces(detail.newValue as number) ?? '',
        };
    }
    return null;
}

export const formatValue = (
    // biome-ignore lint/suspicious/noExplicitAny: value can be anything
    value: any,
    formatter: string | ValueFormatterFunc | undefined,
    // biome-ignore lint/suspicious/noExplicitAny: value can be anything
    defaultFormatter: (value: any) => string
) => {
    if (typeof formatter === 'function') {
        return formatter({ value } as ValueFormatterParams);
    }
    return defaultFormatter(value);
};

export const prettyNameForAuditLogFieldName = (fieldName: string): string => {
    switch (fieldName) {
        case 'PhaseoutProfileId':
            return 'Phaseout Profile';
        case 'ChainFurnitureId':
            return 'Chain Furniture';
        case 'SalesiVlcy':
            return 'Sales iV';
    }
    return capitalCase(fieldName);
};

export const findMatchingDetail = (
    fieldName: string,
    detail: AuditDetailsModel,
    details: AuditDetailsModel[]
): AuditDetailsModel | null => {
    return details.find((d) => d.fieldName === fieldName && d.correlationId === detail.correlationId) ?? null;
};
